import i18n from "../i18n";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
    Download, Upload, ShieldCheck, History, ClipboardList, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { farmDateIso } from "@/lib/farmTime";
import { useTranslation } from "react-i18next";
import LanguageSelector from "@/components/LanguageSelector";

/**
 * Consolidated tab for Phase 3 + 4 customization:
 *  - Field Mode Access matrix (role × action toggle grid)
 *  - Audit Log viewer (read-only)
 *  - Backup & Restore (export / import JSON)
 */
export default function CustomizationTab() {
    const { t } = useTranslation();
    const [view, setView] = useState("access");
    return (
        <div className="space-y-4" data-testid="customization-tab">
            <Card className="p-4 space-y-3" data-testid="owner-language-settings">
                <div>
                    <h3 className="font-semibold">{t("settings.language_title")}</h3>
                    <p className="text-xs text-muted-foreground">{t("settings.language_help")}</p>
                </div>
                <LanguageSelector testId="owner-language-selector" />
            </Card>
            <div className="flex gap-2 flex-wrap">
                <Button
                    variant={view === "access" ? "default" : "outline"}
                    onClick={() => setView("access")}
                    data-testid="view-access-btn"
                ><ShieldCheck className="w-4 h-4 mr-1" />{t("field_mode_access.title")}</Button>
                <Button
                    variant={view === "audit" ? "default" : "outline"}
                    onClick={() => setView("audit")}
                    data-testid="view-audit-btn"
                ><History className="w-4 h-4 mr-1" />{t("audit.title")}</Button>
                <Button
                    variant={view === "backup" ? "default" : "outline"}
                    onClick={() => setView("backup")}
                    data-testid="view-backup-btn"
                ><ClipboardList className="w-4 h-4 mr-1" />{t("backup.title")}</Button>
            </div>
            {view === "access" && <FieldModeAccess />}
            {view === "audit" && <AuditLog />}
            {view === "backup" && <BackupRestore />}
        </div>
    );
}

function FieldModeAccess() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [saving, setSaving] = useState(null);
    const load = useCallback(async () => {
        try {
            const r = await api.get("/field-mode-permissions");
            setData(r.data);
        } catch { toast.error("Failed to load permissions"); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const toggle = async (role, action) => {
        const set = new Set(role.actions || []);
        set.has(action) ? set.delete(action) : set.add(action);
        setSaving(role.role_id);
        try {
            await api.put(`/field-mode-permissions/${role.role_id}`, { actions: Array.from(set) });
            toast.success(`Updated ${role.role_name}`);
            await load();
        } catch { toast.error("Update failed"); }
        finally { setSaving(null); }
    };

    if (!data) return <div className="p-6 text-muted-foreground">{t("common.loading")}</div>;
    return (
        <Card className="p-4" data-testid="field-mode-access-panel">
            <div className="mb-4">
                <h3 className="font-semibold">{t("field_mode_access.title")}</h3>
                <p className="text-xs text-muted-foreground">{t("field_mode_access.subtitle")}</p>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-left border-b">
                            <th className="p-2 text-xs uppercase tracking-wider font-semibold sticky left-0 bg-background">
                                {t("field_mode_access.role")}
                            </th>
                            {data.actions_catalog.map(a => (
                                <th key={a} className="p-2 text-[10px] uppercase font-semibold text-center min-w-[70px]">
                                    {t(`field.${a}`, a)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.roles.map((r) => (
                            <tr key={r.role_id} className="border-b hover:bg-muted/40" data-testid={`fm-role-row-${r.role_name.toLowerCase()}`}>
                                <td className="p-2 font-medium sticky left-0 bg-background flex items-center gap-2">
                                    {r.role_name}
                                    {saving === r.role_id && <Loader2 className="w-3 h-3 animate-spin text-primary" />}
                                </td>
                                {data.actions_catalog.map(a => {
                                    const on = (r.actions || []).includes(a);
                                    return (
                                        <td key={a} className="p-2 text-center">
                                            <Checkbox
                                                checked={on}
                                                onCheckedChange={() => toggle(r, a)}
                                                data-testid={`fm-toggle-${r.role_name.toLowerCase()}-${a}`}
                                            />
                                        </td>
                                    );
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

function AuditLog() {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [filter, setFilter] = useState("");
    const load = useCallback(async (coll) => {
        try {
            const q = coll ? `?collection=${encodeURIComponent(coll)}&limit=300` : "?limit=300";
            const r = await api.get(`/audit-log${q}`);
            setRows(r.data.entries || []);
        } catch { toast.error("Failed to load audit log"); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const filtered = useMemo(() =>
        rows.filter(r => !filter || (r.summary || "").toLowerCase().includes(filter.toLowerCase())
            || (r.user_email || "").toLowerCase().includes(filter.toLowerCase())
            || (r.collection || "").toLowerCase().includes(filter.toLowerCase())),
        [rows, filter]);

    return (
        <Card className="p-4" data-testid="audit-log-panel">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <h3 className="font-semibold">{t("audit.title")}</h3>
                    <p className="text-xs text-muted-foreground">{i18n.t("batch4.d1c69a859a96")} {rows.length} {i18n.t("batch4.76413b0251f1")}</p>
                </div>
                <Input
                    placeholder={t("common.search")}
                    value={filter} onChange={(e) => setFilter(e.target.value)}
                    className="max-w-xs h-9" data-testid="audit-search"
                />
            </div>
            <div className="overflow-x-auto max-h-[520px] overflow-y-auto">
                <table className="w-full text-sm">
                    <thead className="bg-muted/50 sticky top-0">
                        <tr className="text-left">
                            <th className="p-2 text-xs uppercase font-semibold">{t("audit.timestamp")}</th>
                            <th className="p-2 text-xs uppercase font-semibold">{t("audit.user")}</th>
                            <th className="p-2 text-xs uppercase font-semibold">{t("audit.collection")}</th>
                            <th className="p-2 text-xs uppercase font-semibold">{t("audit.action")}</th>
                            <th className="p-2 text-xs uppercase font-semibold">{t("audit.summary")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filtered.length === 0 && <tr><td colSpan={5} className="text-center text-muted-foreground py-6">{i18n.t("batch4.f0d72cd740a1")}</td></tr>}
                        {filtered.map(r => (
                            <tr key={r.id} className="border-t" data-testid={`audit-row-${r.id}`}>
                                <td className="p-2 text-xs whitespace-nowrap">{(r.timestamp || "").slice(0, 19).replace("T", " ")}</td>
                                <td className="p-2 text-xs">
                                    <div className="font-medium">{r.user_email || "—"}</div>
                                    <div className="text-muted-foreground">{r.user_role || ""}</div>
                                </td>
                                <td className="p-2"><Badge variant="outline" className="text-xs">{r.collection}</Badge></td>
                                <td className="p-2 text-xs capitalize">{(r.action || "").replace(/_/g, " ")}</td>
                                <td className="p-2 text-xs">{r.summary}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </Card>
    );
}

function BackupRestore() {
    const { t } = useTranslation();
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    const doExport = async () => {
        setBusy(true);
        try {
            const r = await api.get("/admin/export-settings");
            const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `aj-farm-settings-${farmDateIso()}.json`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success("Settings exported");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Export failed");
        } finally { setBusy(false); }
    };

    const doImport = async (file) => {
        if (!file) return;
        if (!window.confirm("Importing will OVERWRITE existing settings by id. Continue?")) return;
        setBusy(true);
        try {
            const text = await file.text();
            const payload = JSON.parse(text);
            const r = await api.post("/admin/import-settings", payload);
            toast.success(`Imported: ${Object.entries(r.data.counts || {}).map(([k, v]) => `${k}=${v}`).join(", ")}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Import failed — check the file is a valid export JSON");
        } finally { setBusy(false); }
    };

    return (
        <Card className="p-6 space-y-4" data-testid="backup-restore-panel">
            <div>
                <h3 className="font-semibold">{t("backup.title")}</h3>
                <p className="text-xs text-muted-foreground">{i18n.t("batch4.c83ecaf035fc")}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <Button
                    onClick={doExport} disabled={busy}
                    className="h-16 justify-start px-6" size="lg"
                    data-testid="export-settings-btn"
                >
                    {busy ? <Loader2 className="w-5 h-5 mr-3 animate-spin" /> : <Download className="w-5 h-5 mr-3" />}
                    <div className="text-left">
                        <div className="font-semibold">{t("backup.export_btn")}</div>
                        <div className="text-xs opacity-80">{i18n.t("batch4.51c4d347bd7d")}</div>
                    </div>
                </Button>
                <label className="cursor-pointer">
                    <input
                        ref={fileRef} type="file" accept="application/json,.json"
                        className="hidden"
                        onChange={(e) => doImport(e.target.files?.[0])}
                        data-testid="import-settings-input"
                    />
                    <Button
                        onClick={() => fileRef.current?.click()} disabled={busy}
                        variant="outline"
                        className="h-16 justify-start px-6 w-full" size="lg"
                        data-testid="import-settings-btn"
                    >
                        {busy ? <Loader2 className="w-5 h-5 mr-3 animate-spin" /> : <Upload className="w-5 h-5 mr-3" />}
                        <div className="text-left">
                            <div className="font-semibold">{t("backup.import_btn")}</div>
                            <div className="text-xs opacity-80">{t("backup.import_hint")}</div>
                        </div>
                    </Button>
                </label>
            </div>
        </Card>
    );
}
